import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import api, { formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TYPE_LABEL = {
  percentage: "Persentase (%)",
  fixed: "Potongan Tetap (Rp)",
  free_shipping: "Gratis Ongkir",
};

const emptyV = {
  code: "",
  name: "",
  type: "percentage",
  value: 10,
  min_purchase: 0,
  max_discount: null,
  usage_limit: null,
  per_user_limit: null,
  valid_from: "",
  valid_until: "",
  is_active: true,
  auto_apply: false,
  show_in_checkout: true,
  description: "",
};

function VoucherForm({ initial, onSaved }) {
  const [form, setForm] = useState(() => ({ ...emptyV, ...(initial || {}) }));
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.value = Number(payload.value) || 0;
      payload.min_purchase = Number(payload.min_purchase) || 0;
      payload.max_discount = payload.max_discount === "" || payload.max_discount == null ? null : Number(payload.max_discount);
      payload.usage_limit = payload.usage_limit === "" || payload.usage_limit == null ? null : Number(payload.usage_limit);
      payload.per_user_limit = payload.per_user_limit === "" || payload.per_user_limit == null ? null : Number(payload.per_user_limit);
      payload.valid_from = payload.valid_from || null;
      payload.valid_until = payload.valid_until || null;
      if (initial?.id) await api.put(`/admin/vouchers/${initial.id}`, payload);
      else await api.post("/admin/vouchers", payload);
      toast.success("Voucher tersimpan");
      onSaved?.();
    } catch (e) { notifyError(e); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Kode Voucher *</Label><Input value={form.code} onChange={(e) => upd("code", e.target.value.toUpperCase())} required data-testid="voucher-code-input" /></div>
        <div>
          <Label>Tipe</Label>
          <Select value={form.type} onValueChange={(v) => upd("type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Nama Tampil (opsional)</Label><Input value={form.name || ""} onChange={(e) => upd("name", e.target.value)} placeholder="Contoh: Welcome Discount 15%" data-testid="voucher-name-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Nilai {form.type === "percentage" ? "(%)" : form.type === "fixed" ? "(Rp)" : ""}</Label><Input type="number" value={form.value} onChange={(e) => upd("value", e.target.value)} disabled={form.type === "free_shipping"} data-testid="voucher-value-input" /></div>
        <div><Label>Min Pembelian (Rp)</Label><Input type="number" value={form.min_purchase} onChange={(e) => upd("min_purchase", e.target.value)} data-testid="voucher-min-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Max Diskon (Rp)</Label><Input type="number" value={form.max_discount ?? ""} onChange={(e) => upd("max_discount", e.target.value)} placeholder="-" data-testid="voucher-max-input" /></div>
        <div><Label>Limit Pemakaian Total</Label><Input type="number" value={form.usage_limit ?? ""} onChange={(e) => upd("usage_limit", e.target.value)} placeholder="Tanpa batas" data-testid="voucher-usage-input" /></div>
      </div>
      <div><Label>Limit per User (max pakai per customer)</Label><Input type="number" value={form.per_user_limit ?? ""} onChange={(e) => upd("per_user_limit", e.target.value)} placeholder="Tanpa batas" data-testid="voucher-per-user-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Berlaku Mulai (ISO)</Label><Input value={form.valid_from || ""} onChange={(e) => upd("valid_from", e.target.value)} placeholder="2026-06-01T00:00:00Z" data-testid="voucher-from-input" /></div>
        <div><Label>Berlaku Sampai (ISO)</Label><Input value={form.valid_until || ""} onChange={(e) => upd("valid_until", e.target.value)} placeholder="2026-12-31T23:59:59Z" data-testid="voucher-until-input" /></div>
      </div>
      <div><Label>Deskripsi</Label><Textarea value={form.description || ""} onChange={(e) => upd("description", e.target.value)} rows={2} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
        <Label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.is_active} onCheckedChange={(v) => upd("is_active", v)} data-testid="voucher-active-toggle" /> <span>Aktif</span></Label>
        <Label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.show_in_checkout} onCheckedChange={(v) => upd("show_in_checkout", v)} data-testid="voucher-show-toggle" /> <span>Tampil di Picker Checkout</span></Label>
        <Label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.auto_apply} onCheckedChange={(v) => upd("auto_apply", v)} data-testid="voucher-auto-toggle" /> <span>Auto Apply (kandidat terbaik)</span></Label>
      </div>
      <DialogFooter><Button type="submit" className="brand-btn-primary rounded-xl" data-testid="voucher-save-btn">Simpan</Button></DialogFooter>
    </form>
  );
}

export default function AdminPromos() {
  const [vouchers, setVouchers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/vouchers").then((r) => setVouchers(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Hapus voucher?")) return;
    try {
      await api.delete(`/admin/vouchers/${id}`);
      toast.success("Voucher dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black">Promo & Voucher</h1>
          <p className="text-sm text-[#5C5C5C]">{vouchers.length} voucher dibuat</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button className="brand-btn-primary rounded-xl" data-testid="add-voucher-btn" data-tour="promo-page"><Plus size={14} /> Tambah Voucher</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Voucher</DialogTitle></DialogHeader>
            <VoucherForm initial={editing} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vouchers.map((v) => (
          <div key={v.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-5 relative" data-testid={`voucher-card-${v.code}`}>
            {v.auto_apply && (
              <span className="absolute -top-2 left-4 px-2 py-0.5 rounded-md text-[10px] font-black tracking-wider uppercase bg-gradient-to-r from-[#E85D04] to-[#DC2F02] text-white flex items-center gap-1 shadow" data-testid={`voucher-auto-badge-${v.code}`}>
                <Sparkles size={10} /> Auto Apply
              </span>
            )}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#E85D04] font-bold">{TYPE_LABEL[v.type]}</div>
                <div className="font-display font-black text-xl tracking-tight mt-1">{v.code}</div>
                {v.name && <div className="text-xs text-[#5C5C5C]">{v.name}</div>}
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${v.is_active ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-red-100 text-red-700"}`}>{v.is_active ? "Aktif" : "Nonaktif"}</span>
            </div>
            <p className="text-sm text-[#5C5C5C] mt-2">{v.description || "—"}</p>
            <div className="mt-3 text-xs space-y-0.5">
              <div>Nilai: <b>{v.type === "percentage" ? `${v.value}%` : v.type === "fixed" ? `Rp ${Number(v.value).toLocaleString("id-ID")}` : "Gratis Ongkir"}</b></div>
              <div>Min belanja: Rp {Number(v.min_purchase || 0).toLocaleString("id-ID")}</div>
              <div>Dipakai: {v.used_count || 0} {v.usage_limit ? `/ ${v.usage_limit}` : ""} {v.per_user_limit ? `· ${v.per_user_limit}x/user` : ""}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {!v.show_in_checkout && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Hidden Picker</span>}
                {v.valid_until && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">s/d {String(v.valid_until).slice(0, 10)}</span>}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => { setEditing(v); setOpen(true); }} className="rounded-lg" data-testid={`voucher-edit-${v.code}`}><Pencil size={12} /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(v.id)} className="rounded-lg text-[#DC2F02]" data-testid={`voucher-delete-${v.code}`}><Trash2 size={12} /></Button>
            </div>
          </div>
        ))}
        {vouchers.length === 0 && <div className="md:col-span-2 lg:col-span-3 text-center py-12 text-[#5C5C5C] bg-white rounded-2xl border border-[#E5E5E5]">Belum ada voucher</div>}
      </div>
    </AdminLayout>
  );
}
